<style lang="scss">
  .scroll {
    overflow: auto;
    display: flex;
    flex-direction: column;
  }
</style>

<script>
  import { onDestroy, tick } from "svelte";
  import { makeEscapeAction, makeWindowClick } from "../../../utils/escapable";
  import Button from "../../atoms/Button.svelte";
  import ButtonWithTooltip from "../../atoms/ButtonWithTooltip.svelte";
  import FloatingUI from "../../utils/FloatingUI.svelte";
  import PureButton from "../../utils/PureButton.svelte";
  import { ObserverStore } from "./stores";

  let show = false;
  let el;
  let floatingEl;

  let customTrailingIcon = null;
  export { customTrailingIcon as trailingIcon };
  export let onclick = () => {};
  export let onclose = () => {}; // Callback when dropdown is closed
  export let placement = "bottom";
  export let tooltipPlacement = "top";
  export let dynamicWidth = true;
  export let disabled = false;
  export let flip = true; // pass false to disable automatic flipping on filtering
  export let scrollStyle = "";

  export const close = () => {
    show = false;
    onclose();
  };

  // Close dropdown when page scrolls, only if scroll happens outside the dropdown
  const closeDropdownOnScroll = (event) => {
    if (!floatingEl?.contains(event.target)) close();
  };

  // Toggle scroll event listener based on dropdown state
  const toggleScrollEventListener = (isOpen) => {
    const method = isOpen ? "addEventListener" : "removeEventListener";
    document[method]("scroll", closeDropdownOnScroll, true);
  };

  $: toggleScrollEventListener(show);

  // Clean up event listener if component is destroyed while dropdown is open
  onDestroy(() => {
    if (show)
      document.removeEventListener("scroll", closeDropdownOnScroll, true);
  });

  // Optionally pass an event handler for e.g. stopPropagation
  const onclickInner = async (event) => {
    if (disabled) return;
    show = !show;
    await tick(); // wait for dropdown to be rendered before processing onclick
    onclick(event);
  };

  $: windowClick = makeWindowClick(close, el, floatingEl);
  const escape = makeEscapeAction(close);

  const observerStore = ObserverStore();

  $: $observerStore = el;
  $: style = dynamicWidth ? `width: ${$observerStore.width}px;` : undefined;

  // Set default dropdown trailing icon behaviour if no icon is provided
  $: trailingIcon =
    customTrailingIcon ?? (show ? "fa-chevron-up" : "fa-chevron-down");

  // NB: onclickcapture as floating UI calls stopPropagation on click events so our outside click check won't work when we have nested Dropdowns
</script>

<svelte:window onclickcapture={windowClick} />

{#if $$slots.purebutton}
  <div bind:this={el}>
    <PureButton onclick={onclickInner}>
      <slot name="purebutton" />
    </PureButton>
  </div>
{:else if $$slots.tooltip}
  <ButtonWithTooltip
    bind:el
    class={disabled ? "disabled" : ""}
    {trailingIcon}
    onclick={onclickInner}
    placement={tooltipPlacement}
    {...$$restProps}
  >
    <span slot="tooltip"><slot name="tooltip" /></span>
    <slot name="button" />
  </ButtonWithTooltip>
{:else}
  <Button
    bind:el
    class={disabled ? "disabled" : ""}
    {trailingIcon}
    onclick={onclickInner}
    {...$$restProps}
  >
    <slot name="button" />
  </Button>
{/if}
{#if show}
  <FloatingUI
    referenceEl={el}
    bind:floatingEl
    {escape}
    {style}
    class="dropdown"
    {placement}
    {flip}
  >
    {#if ["bottom", "bottom-start"].includes(placement)}
      <slot name="header" />
    {/if}
    <div class="scroll" style={scrollStyle}>
      <slot />
    </div>
    {#if placement === "top"}
      <slot name="header" />
    {/if}
  </FloatingUI>
{/if}
